import React, { useCallback, useRef, useState } from 'react';
import { CheckCircle2, Loader2, UploadCloud, X, XCircle } from 'lucide-react';
import { BarreProgression } from './Chargement';
import { signalerSessionExpiree } from './Systeme';

/* ═══════════════════════════════════════════════════════════════════════
   4. FICHIERS — du choix au fichier reçu

   sélectionné → validé (type, taille) → envoi (pourcentage réel) →
   traitement (le serveur vérifie) → reçu  |  refusé · échec · annulé

   `fetch` ne sait pas dire où en est un envoi : le téléversement passe
   par XMLHttpRequest, seul à exposer la progression montante. Le jeton
   expiré en cours de route est renouvelé une fois, puis l'envoi reprend.
   ═══════════════════════════════════════════════════════════════════════ */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const CLE_JETON = 'asso_acc_token';
const CLE_RENOUVELLEMENT = 'asso_acc_refresh';

const lire = (cle) => { try { return localStorage.getItem(cle); } catch { return null; } };

async function renouvelerJeton() {
  const refresh = lire(CLE_RENOUVELLEMENT);
  if (!refresh) return false;
  try {
    const r = await fetch(`${API_BASE}/accompagnement/auth/token/refresh/`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh }),
    });
    if (!r.ok) return false;
    const d = await r.json();
    localStorage.setItem(CLE_JETON, d.access);
    if (d.refresh) localStorage.setItem(CLE_RENOUVELLEMENT, d.refresh);
    return true;
  } catch { return false; }
}

export const tailleLisible = (octets) => {
  if (!octets && octets !== 0) return '';
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
};

/** Vérifier un fichier AVANT de l'envoyer. Renvoie un message, ou null. */
export function validerFichier(fichier, { extensions = [], tailleMax } = {}) {
  if (!fichier) return 'Aucun fichier choisi.';
  if (extensions.length) {
    const nom = fichier.name.toLowerCase();
    if (!extensions.some((e) => nom.endsWith(e.toLowerCase()))) {
      const recu = (nom.split('.').pop() || '').toUpperCase();
      // Au-delà de six formats, la liste noie l'information : on dit ce qui est refusé.
      return extensions.length > 6
        ? `Le format ${recu ? `« ${recu} » ` : ''}n’est pas accepté. Choisissez un PDF, un document bureautique, une image, un CSV ou un ZIP.`
        : `Format non accepté. Formats attendus : ${extensions.join(', ').replace(/\./g, '').toUpperCase()}.`;
    }
  }
  if (tailleMax && fichier.size > tailleMax) {
    return `Le fichier fait ${tailleLisible(fichier.size)} ; la limite est de ${tailleLisible(tailleMax)}.`;
  }
  if (fichier.size === 0) return 'Le fichier est vide.';
  return null;
}

/** Envoi avec progression. Résout { statut, corps } ; lève si le réseau échoue. */
export function televerser({ url, donnees, methode = 'POST', onProgression, signal }) {
  const envoyer = () => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(methode, url);
    const jeton = lire(CLE_JETON);
    if (jeton) xhr.setRequestHeader('Authorization', `Bearer ${jeton}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgression?.(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let corps = {};
      try { corps = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { corps = {}; }
      resolve({ statut: xhr.status, corps });
    };
    xhr.onerror = () => reject(new Error('Le serveur ne répond pas. Vérifiez votre connexion, puis réessayez.'));
    xhr.onabort = () => { const e = new Error('Envoi annulé.'); e.annule = true; reject(e); };
    if (signal) {
      if (signal.aborted) { xhr.abort(); return; }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(donnees);
  });

  return envoyer().then(async (r) => {
    if (r.statut !== 401) return r;
    if (await renouvelerJeton()) { onProgression?.(0); return envoyer(); }
    signalerSessionExpiree();
    return r;
  });
}

/* Une validation DRF répond { champ: ['message'] } sans clé `error` : on
   en tire la première phrase, plutôt qu'un « erreur 400 » qui ne dit rien. */
function premierMessageDeChamp(corps) {
  if (!corps || typeof corps !== 'object') return '';
  for (const valeur of Object.values(corps)) {
    if (typeof valeur === 'string' && valeur) return valeur;
    if (Array.isArray(valeur) && typeof valeur[0] === 'string') return valeur[0];
  }
  return '';
}

/** L'état d'un téléversement, pour un écran. */
export function useTeleversement() {
  const [etat, setEtat] = useState({ statut: 'inactif', progression: 0, erreur: null, reponse: null });
  const controleur = useRef(null);

  const lancer = useCallback(async ({ url, donnees, methode }) => {
    controleur.current = new AbortController();
    setEtat({ statut: 'envoi', progression: 0, erreur: null, reponse: null });
    try {
      const r = await televerser({
        url, donnees, methode, signal: controleur.current.signal,
        onProgression: (p) => setEtat((e) => ({ ...e, progression: p, statut: p >= 100 ? 'traitement' : 'envoi' })),
      });
      if (r.statut >= 200 && r.statut < 300) {
        setEtat({ statut: 'termine', progression: 100, erreur: null, reponse: r.corps });
        return r.corps;
      }
      const message = r.corps?.error || r.corps?.detail || premierMessageDeChamp(r.corps)
        || (r.statut === 413 ? 'Le fichier est trop volumineux pour le serveur.' : `Le fichier n’a pas été reçu (erreur ${r.statut}).`);
      setEtat({ statut: 'echec', progression: 0, erreur: message, reponse: r.corps });
      const e = new Error(message); e.statut = r.statut; e.details = r.corps; throw e;
    } catch (e) {
      if (e.annule) { setEtat({ statut: 'annule', progression: 0, erreur: null, reponse: null }); throw e; }
      setEtat((x) => (x.statut === 'echec' ? x : { statut: 'echec', progression: 0, erreur: e.message, reponse: null }));
      throw e;
    }
  }, []);

  const annuler = useCallback(() => { setEtat((e) => ({ ...e, statut: 'annulation' })); controleur.current?.abort(); }, []);
  const reinitialiser = useCallback(() => setEtat({ statut: 'inactif', progression: 0, erreur: null, reponse: null }), []);

  return { ...etat, enCours: ['envoi', 'traitement', 'annulation'].includes(etat.statut), lancer, annuler, reinitialiser };
}

/** Télécharger avec progression (quand le serveur donne la taille). */
export async function telecharger(url, nomFichier, { onProgression } = {}) {
  const appel = () => fetch(url, { headers: lire(CLE_JETON) ? { Authorization: `Bearer ${lire(CLE_JETON)}` } : {} });
  let r = await appel();
  if (r.status === 401 && await renouvelerJeton()) r = await appel();
  if (r.status === 401) { signalerSessionExpiree(); throw Object.assign(new Error('Session expirée.'), { statut: 401 }); }
  if (!r.ok) {
    const corps = await r.json().catch(() => ({}));
    throw Object.assign(new Error(corps.error || corps.detail || `Le téléchargement a échoué (erreur ${r.status}).`), { statut: r.status });
  }
  const total = Number(r.headers.get('Content-Length')) || 0;
  let blob;
  if (r.body && total && onProgression) {
    const lecteur = r.body.getReader();
    const morceaux = [];
    let recu = 0;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await lecteur.read();
      if (done) break;
      morceaux.push(value);
      recu += value.length;
      onProgression(Math.round((recu / total) * 100));
    }
    blob = new Blob(morceaux, { type: r.headers.get('Content-Type') || 'application/octet-stream' });
  } else {
    onProgression?.(null);
    blob = await r.blob();
  }
  const lien = document.createElement('a');
  lien.href = URL.createObjectURL(blob);
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(lien.href), 1500);
  onProgression?.(100);
}

/** La zone où l'on dépose (ou choisit) un fichier.
 *  États : inactif · survol (prêt à recevoir) · refuse · accepte. */
export function ZoneDepot({ onFichier, extensions = [], tailleMax, desactive = false, texte, aide, fichier, onRetirer }) {
  const [etat, setEtat] = useState('inactif');
  const [refus, setRefus] = useState('');
  const champ = useRef(null);

  const recevoir = (f) => {
    const probleme = validerFichier(f, { extensions, tailleMax });
    if (probleme) { setEtat('refuse'); setRefus(probleme); return; }
    setRefus('');
    setEtat('accepte');
    onFichier(f);
  };

  const formats = extensions.length ? extensions.join(', ').replace(/\./g, '').toUpperCase() : 'Tout format';
  return (
    <div>
      <div
        className={`zone-depot relative p-5 text-center cursor-pointer ${desactive ? 'opacity-50 pointer-events-none' : ''}`}
        data-etat={etat}
        role="button" tabIndex={0} aria-disabled={desactive || undefined}
        aria-label={texte || 'Choisir un fichier'}
        onClick={() => champ.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); champ.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); if (etat !== 'survol') setEtat('survol'); }}
        onDragLeave={() => setEtat(fichier ? 'accepte' : 'inactif')}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) recevoir(f); else setEtat('inactif'); }}
      >
        <input ref={champ} type="file" className="hidden" accept={extensions.join(',') || undefined}
               onChange={(e) => { const f = e.target.files?.[0]; if (f) recevoir(f); e.target.value = ''; }} />
        {fichier ? (
          <div className="flex items-center gap-3 text-left">
            <CheckCircle2 className="w-6 h-6 shrink-0 text-[var(--accent-texte)]" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="m-0 text-[13.5px] font-semibold text-[var(--text-bold)] truncate">{fichier.name}</p>
              <p className="m-0 text-[12px] text-[var(--text-muted)]">{tailleLisible(fichier.size)} · prêt à être envoyé</p>
            </div>
            {onRetirer && (
              <button type="button" onClick={(e) => { e.stopPropagation(); setEtat('inactif'); onRetirer(); }}
                      className="act act-icone act-sm act-tertiaire" aria-label="Retirer ce fichier">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <>
            <UploadCloud className={`w-8 h-8 mx-auto mb-2 ${etat === 'survol' ? 'text-[var(--accent-texte)]' : 'text-[var(--text-muted)]'}`} aria-hidden="true" />
            <p className="m-0 text-[13.5px] font-semibold text-[var(--text-bold)]">
              {etat === 'survol' ? 'Déposez le fichier ici' : texte || 'Glissez un fichier ici ou cliquez pour choisir'}
            </p>
            <p className="m-0 mt-1 text-[12px] text-[var(--text-muted)]">
              {aide || `${formats}${tailleMax ? ` · ${tailleLisible(tailleMax)} max.` : ''}`}
            </p>
          </>
        )}
      </div>
      {refus && (
        <p role="alert" className="mt-2 mb-0 flex items-start gap-1.5 text-[12.5px] text-[var(--alerte)]">
          <XCircle className="w-4 h-4 shrink-0 mt-[1px]" aria-hidden="true" />{refus}
        </p>
      )}
    </div>
  );
}

/** Où en est l'envoi : progression, traitement, reçu, échec — et Annuler. */
export function SuiviTeleversement({ televersement, nom, annulable = true }) {
  const { statut, progression, erreur, annuler } = televersement;
  if (statut === 'inactif') return null;
  if (statut === 'termine') {
    return <p role="status" className="m-0 flex items-center gap-1.5 text-[13px] font-semibold text-[var(--accent-texte)]"><CheckCircle2 className="w-4 h-4" />{nom ? `« ${nom} » reçu.` : 'Fichier reçu.'}</p>;
  }
  if (statut === 'echec') {
    return <p role="alert" className="m-0 flex items-start gap-1.5 text-[13px] text-[var(--alerte)]"><XCircle className="w-4 h-4 shrink-0 mt-[1px]" />{erreur}</p>;
  }
  if (statut === 'annule') return <p role="status" className="m-0 text-[13px] text-[var(--text-muted)]">Envoi annulé.</p>;
  return (
    <div className="flex items-end gap-3">
      <BarreProgression className="flex-1"
                        valeur={statut === 'envoi' ? progression : null}
                        libelle={statut === 'traitement' ? 'Vérification par le serveur…' : statut === 'annulation' ? 'Annulation…' : 'Envoi en cours…'} />
      {statut === 'envoi' && annulable && (
        <button type="button" onClick={annuler} className="act act-sm act-tertiaire shrink-0">Annuler</button>
      )}
      {statut === 'traitement' && <Loader2 className="w-4 h-4 animate-spin text-[var(--text-muted)] mb-0.5" aria-hidden="true" />}
    </div>
  );
}
